import {AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import { colors } from '../../../../colors.const';
import { CoreConfigService } from '../../../../../@core/services/config.service';
import {ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexFill, ApexGrid, ApexLegend, ApexMarkers, ApexPlotOptions, ApexStroke, ApexTheme, ApexTitleSubtitle, ApexTooltip, ApexXAxis, ApexYAxis, ChartComponent} from 'ng-apexcharts';
import * as _ from 'lodash';
import {BehaviorSubject, Subject, Subscription} from "rxjs";
import {debounceTime} from "rxjs/operators";

// interface ChartOptions
export interface ChartOptions {
  series?: ApexAxisChartSeries;
  chart?: ApexChart;
  xaxis?: ApexXAxis;
  dataLabels?: ApexDataLabels;
  grid?: ApexGrid;
  stroke?: ApexStroke;
  legend?: ApexLegend;
  title?: ApexTitleSubtitle;
  colors?: string[];
  tooltip?: ApexTooltip;
  plotOptions?: ApexPlotOptions;
  yaxis?: ApexYAxis;
  fill?: ApexFill;
  labels?: string[];
  markers: ApexMarkers;
  theme: ApexTheme;
}

export interface CustomSteplineSeries {
  name: string;
  data: any[];
  color?: string;
}

@Component({
  selector: 'app-custom-stepline',
  templateUrl: './custom-stepline.component.html',
  styleUrls: ['./custom-stepline.component.scss']
})
export class CustomSteplineComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() data = [];
  @Input() title = "";
  @Input() threshold = 0;
  @Input() label = "";
  @Input() compact = false;
  @Input() compactHeight = 150;
  @Input() reduceGrid = false;
  @Input() showTimeAxis = true;
  /** Series opcionales usadas por el gráfico de alarmas; el formato anterior sigue vigente. */
  @Input() alarmSeries: CustomSteplineSeries[] = [];
  @Input() showLegend = false;
  public isMenuToggled = false;
  subscriptions: Subscription[] = [];
  changes$ = new BehaviorSubject<SimpleChanges>({});

  @ViewChild(ChartComponent, {static: true}) apexLineChartRef: any;
  // Apex Line Area Chart
  apexLineChart: Partial<ChartOptions> = {
    series: [
      {
        name: 'data',
        data: []
      },
      // {
      //   data: [280, 200, 220, 180, 270, 250, 70, 90, 200, 150, 160, 100, 150, 100, 50]
      // }
      {
        name: 'umbral',
        data: []
      }
    ],
    chart: {
      height: 400,
      type: 'line',
      zoom: {
        enabled: false
      },
      toolbar: {
        show: false
      }
    },
    grid: {
      padding: {
        left: 12,
        right: 12
      },
      xaxis: {
        lines: {
          show: true
        }
      }
    },
    markers: {
      size: 0,
      hover: {
        size: 5,
        sizeOffset: 2
      },
      strokeWidth: 2,
      strokeOpacity: 1,
      strokeColors: [colors.solid.white],
      colors: [colors.solid.danger]
    },
    colors: [colors.solid.danger],
    dataLabels: {
      enabled: false
    },
    legend: {
      show: false
    },
    stroke: {
      curve: 'stepline',
      width: 2,
      dashArray: [0, 2]
    },
    xaxis: {
      tickAmount: 18,
      axisTicks: {
        show: true
      },
      labels: {
        hideOverlappingLabels: true,
        showDuplicates: false,
        rotate: 0,
        trim: true
      },
      crosshairs: {
        show: true,
        position: 'back',
        stroke: {
          color: '#19bd79',
          width: 1,
          dashArray: 3
        }
      },
      categories: [
        // '7/12',
        //   '8/12',
        //   '9/12',
        //   '10/12',
        //   '11/12',
        //   '12/12',
        //   '13/12',
        //   '14/12',
        //   '15/12',
        //   '16/12',
        //   '17/12',
        //   '18/12',
        //   '19/12',
        //   '20/12',
        //   '21/12'
      ]
    },
    yaxis: {
      min: 0,
      max: 1,
      tickAmount: 1,
      forceNiceScale: true,
      labels: {
        formatter: (value: number) => this.formatYAxisValue(value)
      }
    },
    tooltip: {
      enabled: true,
      followCursor: false,
      intersect: false,
      shared: false,
      x: {
        formatter: (value: number) => this.formatTooltipMinute(value)
      },
      y: {
        formatter: (value: number) => Number(value) > 0 ? 'Alarma detectada' : 'Sin alarma'
      }
    }
  };

  constructor(
    private _coreConfigService: CoreConfigService
  ) {

  }



  ngOnInit(): void {
    console.log("CUSTOM STEPLINE")
  }

  getWidth(){
    try{
      return this.isMenuToggled === false ? this.apexLineChartRef.offsetWidth : this.apexLineChart.chart.width
    }catch(err){}
    return _.get(this.apexLineChart, 'chart.width', 0);
  }

  /**
   * ng-apexcharts no expone `height` como input independiente; la altura debe
   * viajar dentro de la configuración `chart` para que ApexCharts la aplique.
   */
  get chartRenderHeight(): number {
    const configuredHeight = Number(this.compactHeight);
    const totalHeight = Number.isFinite(configuredHeight) ? configuredHeight : 150;
    const axisHeight = this.compact && this.reduceGrid && this.showTimeAxis ? 24 : 0;
    return this.compact ? Math.max(40, totalHeight - axisHeight) : 360;
  }

  /**
   * After View Init
   */
  ngAfterViewInit() {
    // Subscribe to core config changes
    let configSub = this._coreConfigService.getConfig().subscribe(config => {
      // If Menu Collapsed Changes
      if (config.layout.menu.collapsed === true || config.layout.menu.collapsed === false) {
        setTimeout(() => {
          // Get Dynamic Width for Charts
          this.isMenuToggled = true;
          this.apexLineChart.chart['width'] = _.get(this.apexLineChartRef, 'nativeElement.offsetWidth', this.apexLineChart.chart['width']);
        }, 900);
      }
    });
    this.subscriptions.push(configSub);
    this.subscriptions.push(
      this.changes$.pipe(
        debounceTime(100)
      ).subscribe((changes)=>{
        this._ngOnChanges(changes)
      })
    )
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub=>sub.unsubscribe());
    if(this.apexLineChartRef) this.apexLineChartRef.destroy();
  }

  dataLoaded = false;

  ngOnChanges(changes: SimpleChanges): void {
    this.changes$.next(changes);
  }

  _ngOnChanges(changes: SimpleChanges){
    const useSparseNumericAxis = this.reduceGrid;
    const maxTickAmount = this.compact ? 4 : 10;
    const dayEndMinute = 24 * 60;

    this.apexLineChart.xaxis.type = useSparseNumericAxis ? 'numeric' : 'category';
    this.apexLineChart.xaxis.tickAmount = useSparseNumericAxis ? maxTickAmount : undefined;
    (this.apexLineChart.xaxis as any).stepSize = undefined;
    this.apexLineChart.xaxis.min = useSparseNumericAxis ? 0 : undefined;
    this.apexLineChart.xaxis.max = useSparseNumericAxis ? dayEndMinute : undefined;
    this.apexLineChart.grid.xaxis.lines.show = !useSparseNumericAxis;
    this.apexLineChart.xaxis.axisTicks.show = !useSparseNumericAxis;
    this.apexLineChart.xaxis.labels.hideOverlappingLabels = true;
    this.apexLineChart.xaxis.labels.showDuplicates = false;
    this.apexLineChart.xaxis.labels.show = !useSparseNumericAxis;
    this.apexLineChart.xaxis.labels.trim = false;
    this.apexLineChart.xaxis.categories = [];
    this.apexLineChart.series[0].data = [];
    const data = [];
    const tdata = [];
    const numericData = [];
    const renderData = this.data.map((row: any) => ({ ...row }));
    const configuredSeries = Array.isArray(this.alarmSeries) && this.alarmSeries.length
      ? this.alarmSeries.map(series => ({ ...series, data: (series.data || []).map(row => ({ ...row })) }))
      : null;
    const axisLabels = (configuredSeries ? configuredSeries[0].data : renderData).map((row: any) => row.label);

    this.apexLineChart.xaxis.labels.formatter = useSparseNumericAxis
      ? (value: string) => this.formatMinuteLabel(Number(value), axisLabels)
      : undefined;
    if (configuredSeries) {
      const renderedSeries = configuredSeries.map((configured, seriesIndex) => {
        const seriesData = [];
        configured.data.forEach((row: any, index: number) => {
          const numericValue = Number(row.value);
          const value = Number.isFinite(numericValue) ? numericValue : 0;
          numericData.push(value);
          seriesData.push(useSparseNumericAxis ? {x: index, y: value} : value);
          if (!useSparseNumericAxis && seriesIndex === 0) this.apexLineChart.xaxis.categories.push(row.label);
        });
        return { name: configured.name || 'Alarma', data: seriesData };
      });
      this.apexLineChart.series = renderedSeries;
      this.apexLineChart.colors = configuredSeries.map(series => series.color || colors.solid.danger);
      this.apexLineChart.markers = { ...this.apexLineChart.markers,
        colors: this.apexLineChart.colors };
      this.apexLineChart.stroke = { ...this.apexLineChart.stroke, curve: 'stepline', width: 2,
        dashArray: renderedSeries.map(() => 0) };
      this.apexLineChart.legend = {
        show: this.showLegend && renderedSeries.length > 0,
        position: 'bottom',
        fontSize: this.compact ? '9px' : '11px',
        labels: { colors: '#aebbc9' }
      };
    } else {
      renderData.forEach((row:any, index: number)=>{
        const numericValue = Number(row.value);
        const value = Number.isFinite(numericValue) ? numericValue : 0;
        numericData.push(value);
        data.push(useSparseNumericAxis ? {x: index, y: value} : value);
        if (!useSparseNumericAxis) this.apexLineChart.xaxis.categories.push(row.label);
        tdata.push(useSparseNumericAxis ? {x: index, y: this.threshold} : this.threshold);
      });
      this.apexLineChart.series = [
        { name: this.label, data },
        { name: 'umbral', data: tdata }
      ];
      this.apexLineChart.colors = [colors.solid.danger];
      this.apexLineChart.markers = { ...this.apexLineChart.markers, colors: [colors.solid.danger] };
      this.apexLineChart.stroke = { ...this.apexLineChart.stroke, curve: 'stepline', width: 2, dashArray: [0, 2] };
      this.apexLineChart.legend = { show: false };
    }
    const finiteThreshold = Number.isFinite(Number(this.threshold)) ? Number(this.threshold) : 0;
    const maximumValue = Math.max(0, finiteThreshold, ...numericData);
    const yAxisMaximum = this.getNiceYAxisMaximum(maximumValue);
    this.apexLineChart.yaxis = {
      min: 0,
      max: yAxisMaximum,
      tickAmount: this.getYAxisTickAmount(yAxisMaximum),
      forceNiceScale: true,
      decimalsInFloat: yAxisMaximum <= 1 ? 1 : 0,
      labels: {
        minWidth: this.compact ? 24 : 34,
        maxWidth: this.compact ? 48 : 72,
        formatter: (value: number) => this.formatYAxisValue(value)
      }
    };
    this.apexLineChart.grid = {
      ...this.apexLineChart.grid,
      padding: {
        ...this.apexLineChart.grid.padding,
        left: this.compact && yAxisMaximum >= 1000 ? 18 : 12,
        right: 12
      }
    };
    console.log("custom stepline changes", {chart: this.apexLineChart, data: data, tdata: tdata});
    this.dataLoaded = true;
  }

  private getNiceYAxisMaximum(value: number): number {
    if (!Number.isFinite(value) || value <= 1) return 1;
    if (value <= 5) return Math.ceil(value);

    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    const normalized = value / magnitude;
    const niceNormalized = normalized <= 1
      ? 1
      : normalized <= 2
        ? 2
        : normalized <= 5
          ? 5
          : 10;
    return niceNormalized * magnitude;
  }

  private getYAxisTickAmount(maximum: number): number {
    if (maximum <= 1) return 1;
    if (maximum <= 5) return Math.round(maximum);
    return this.compact ? 3 : 5;
  }

  private formatYAxisValue(value: number): string {
    if (!Number.isFinite(value)) return '0';
    const absoluteValue = Math.abs(value);
    if (absoluteValue >= 1000000) {
      return `${this.trimAxisDecimal(value / 1000000)}M`;
    }
    if (absoluteValue >= 1000) {
      return `${this.trimAxisDecimal(value / 1000)}K`;
    }
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(1).replace(/\.0$/, '');
  }

  private trimAxisDecimal(value: number): string {
    return value.toFixed(Math.abs(value) < 10 ? 1 : 0).replace(/\.0$/, '');
  }

  private formatMinuteLabel(value: number, axisLabels: string[]): string {
    const minute = Math.max(0, Math.round(value));
    const existingLabel = axisLabels[minute];

    if (existingLabel) {
      return existingLabel.slice(0, 5);
    }

    if (minute === 24 * 60) return '24:00';

    const hours = Math.floor(minute / 60) % 24;
    const minutes = minute % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private formatTooltipMinute(value: number): string {
    if (!Number.isFinite(Number(value))) return '';
    const minute = Math.max(0, Math.round(Number(value)));
    if (minute === 24 * 60) return '24:00';
    const hours = Math.floor(minute / 60) % 24;
    const minutes = minute % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private isTickLabel(value: string, interval: number): boolean {
    if (typeof value !== 'string' || value.length === 0) {
      return false;
    }

    const parts = value.split(':');
    if (parts.length !== 2) {
      return false;
    }

    const minutes = Number(parts[0]) * 60 + Number(parts[1]);
    return isFinite(minutes) && minutes % interval === 0;
  }
}
